import type { Network } from "@/lib/tool-kit/physics-waves/resistor-network";

/**
 * Lays a resistor network out as a schematic: series children left to right, parallel
 * children stacked between two rails. Pure geometry, so the drawing is the same on the
 * server and in the browser and can be tested without a DOM.
 */

export interface Wire {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface Part {
  x: number;
  y: number;
  /** Position of this resistor in reading order, from 0. */
  index: number;
  ohms: number;
}

export interface Schematic {
  width: number;
  height: number;
  wires: Wire[];
  parts: Part[];
}

export const PART_WIDTH = 84;
export const PART_HEIGHT = 30;
const WIRE = 18;
const RAIL = 14;
const ROW_GAP = 22;
const MARGIN = 16;
/** Room above the first row for its "R1" tag. */
const TAG = 14;

interface Size {
  w: number;
  h: number;
  /** Distance from the top of the box to its connecting wire. */
  cy: number;
}

function sizeOf(network: Network): Size {
  if (network.kind === "leaf") {
    return { w: PART_WIDTH, h: PART_HEIGHT + TAG, cy: TAG + PART_HEIGHT / 2 };
  }
  const sizes = network.children.map(sizeOf);
  if (network.kind === "series") {
    const above = Math.max(...sizes.map((s) => s.cy));
    const below = Math.max(...sizes.map((s) => s.h - s.cy));
    const w =
      sizes.reduce((sum, s) => sum + s.w, 0) + WIRE * (sizes.length - 1);
    return { w, h: above + below, cy: above };
  }
  const w = Math.max(...sizes.map((s) => s.w)) + 2 * RAIL;
  const h =
    sizes.reduce((sum, s) => sum + s.h, 0) + ROW_GAP * (sizes.length - 1);
  const first = sizes[0].cy;
  const last = sizes.at(-1) ?? sizes[0];
  return { w, h, cy: (first + (h - last.h) + last.cy) / 2 };
}

interface Cursor {
  wires: Wire[];
  parts: Part[];
  next: number;
}

function place(network: Network, x: number, y: number, cursor: Cursor): Size {
  const size = sizeOf(network);
  const wireY = y + size.cy;
  if (network.kind === "leaf") {
    cursor.parts.push({
      x,
      y: y + TAG,
      index: cursor.next,
      ohms: network.ohms,
    });
    cursor.next += 1;
    cursor.wires.push({ x1: x, y1: wireY, x2: x + 12, y2: wireY });
    cursor.wires.push({
      x1: x + PART_WIDTH - 12,
      y1: wireY,
      x2: x + PART_WIDTH,
      y2: wireY,
    });
    return size;
  }
  if (network.kind === "series") {
    let cx = x;
    network.children.forEach((child, i) => {
      const childSize = sizeOf(child);
      place(child, cx, y + size.cy - childSize.cy, cursor);
      cx += childSize.w;
      if (i < network.children.length - 1) {
        cursor.wires.push({ x1: cx, y1: wireY, x2: cx + WIRE, y2: wireY });
        cx += WIRE;
      }
    });
    return size;
  }
  let cy = y;
  const centres: number[] = [];
  const inner = size.w - 2 * RAIL;
  for (const child of network.children) {
    const childSize = sizeOf(child);
    const childX = x + RAIL + (inner - childSize.w) / 2;
    place(child, childX, cy, cursor);
    const childWire = cy + childSize.cy;
    centres.push(childWire);
    cursor.wires.push({ x1: x, y1: childWire, x2: childX, y2: childWire });
    cursor.wires.push({
      x1: childX + childSize.w,
      y1: childWire,
      x2: x + size.w,
      y2: childWire,
    });
    cy += childSize.h + ROW_GAP;
  }
  cursor.wires.push({ x1: x, y1: centres[0], x2: x, y2: centres.at(-1) ?? 0 });
  cursor.wires.push({
    x1: x + size.w,
    y1: centres[0],
    x2: x + size.w,
    y2: centres.at(-1) ?? 0,
  });
  return size;
}

export function layoutNetwork(network: Network): Schematic {
  const size = sizeOf(network);
  const cursor: Cursor = { wires: [], parts: [], next: 0 };
  place(network, MARGIN + WIRE, MARGIN, cursor);
  const wireY = MARGIN + size.cy;
  cursor.wires.push({ x1: MARGIN, y1: wireY, x2: MARGIN + WIRE, y2: wireY });
  cursor.wires.push({
    x1: MARGIN + WIRE + size.w,
    y1: wireY,
    x2: MARGIN + 2 * WIRE + size.w,
    y2: wireY,
  });
  return {
    width: size.w + 2 * (MARGIN + WIRE),
    height: size.h + 2 * MARGIN,
    wires: cursor.wires,
    parts: cursor.parts,
  };
}
